import { Switch } from 'react-router-dom';
import { PendingDataProvider } from '../../providers/PendingData.provider';
import AccountCreation from './AccountCreation/AccountCreationContainer';
import AccountSummaryPage from './AccountSummary/AccountSummaryPage';
import FundAccountPage from './FundAccountNew/FundAccountPage';
import WithdrawAccountPage from './WithdrawAccountNew/WithdrawAccountPage';
import BankAccountTransfer from './BankAccountTransfer/BankAccountTransferContainer';
import AutoBalanceTransferPage from './AutoBalanceTransfer/AutoBalanceTransferPage';
import SetUpTransferPage from './AutoBalanceTransfer/SetUpTransfer/SetUpTransferPage';
import OperationalAccountsPage from './OperationalAccounts/OperationalAccountsPage';
import { usePermissions } from '../../providers/Permissions.provider';
import ProtectedRoute from '../../utils/ProtectedRoute';
import { useLockedFeatures } from '../../providers/LockedFeatures.provider';
import TransferFundsContainer from '../SubAccounts/TransferFunds/TransferFundsContainer';
import { useClientData } from '../../providers/ClientData.provider';
import { JSX } from 'react';
import { useModuleFeatureFlag } from '@/providers/FeatureFlag.provider';
import AccountAndBillingPage from './AccountAndBilling/AccountAndBillingPage';

const Routes = (): JSX.Element => {
  const { permissions } = usePermissions();
  const lockedFeatures = useLockedFeatures();
  const featureFlags = useModuleFeatureFlag('myAccount');

  const {
    response: { SubAccountType },
  } = useClientData();

  return (
    <Switch>
      <ProtectedRoute
        isAllowed={featureFlags.isEnabled && !!permissions.ViewAccountSummary}
        showPermission
        exact
        path="/account-summary"
        component={AccountSummaryPage}
      />

      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.ViewAccountAndBilling && !!lockedFeatures.AccountAndBillingEnabled
        }
        showPermission
        exact
        path="/account-and-billing"
        component={AccountAndBillingPage}
      />

      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled &&
          !!featureFlags.BankAccountTransfer &&
          !!lockedFeatures.BankAccountTransferEnabled &&
          !!permissions.CreateBankAccountTransfer
        }
        showPermission
        exact
        path="/bank-account-transfer"
        component={BankAccountTransfer}
      />

      <ProtectedRoute
        isAllowed={
          featureFlags.isEnabled && !!featureFlags.OperationalAccounts && !!lockedFeatures.OperationalAccountsEnabled
        }
        showPermission
        exact
        path="/operational-accounts"
        component={OperationalAccountsPage}
      />

      <ProtectedRoute
        isAllowed={!!featureFlags.isEnabled}
        showPermission
        exact
        path="/link-my-account"
        component={AccountCreationWithProver}
      />

      <ProtectedRoute
        isAllowed={
          !!lockedFeatures.AutoBalanceTransferEnabled && featureFlags.isEnabled && !!featureFlags.AutoBalanceTransfer
        }
        showPermission
        exact
        path="/auto-balance-transfer"
        component={AutoBalanceTransferPage}
      />

      <ProtectedRoute
        isAllowed={
          !!lockedFeatures.AutoBalanceTransferEnabled &&
          featureFlags.isEnabled &&
          !!featureFlags.AutoBalanceTransfer &&
          !!permissions.CreateAutoBalanceTransfer
        }
        showPermission
        exact
        path="/auto-balance-transfer/set-up/:currency"
      >
        <SetUpTransferPage mode="create" />
      </ProtectedRoute>

      <ProtectedRoute
        isAllowed={
          !!lockedFeatures.AutoBalanceTransferEnabled &&
          featureFlags.isEnabled &&
          !!featureFlags.AutoBalanceTransfer &&
          !!permissions.CreateAutoBalanceTransfer
        }
        showPermission
        exact
        path="/auto-balance-transfer/update/:currency"
      >
        <SetUpTransferPage mode="update" />
      </ProtectedRoute>

      <ProtectedRoute
        isAllowed={
          !!permissions.CreateFundMyAccountTransactions &&
          featureFlags.isEnabled &&
          !!featureFlags.FundMyAccount &&
          !!lockedFeatures.FundMyAccountEnabled
        }
        showPermission
        exact
        path="/fund-my-account"
        component={SubAccountType === 'franchise' ? TransferFundsContainer : FundAccountPage}
      />

      <ProtectedRoute
        isAllowed={
          !!permissions.CreateWithdrawMyAccountTransactions &&
          featureFlags.isEnabled &&
          !!featureFlags.WithdrawMyAccount &&
          !!lockedFeatures.WithdrawMyAccountEnabled
        }
        showPermission
        exact
        path="/withdraw-my-account"
        component={WithdrawAccountPage}
      />
    </Switch>
  );
};

function AccountCreationWithProver(): JSX.Element {
  return (
    <PendingDataProvider>
      <AccountCreation
        onStepChange={() => {}}
        onStepReject={() => {}}
        stepUser={0}
        stepUserChange={false}
        isAllowMove
        isClientControlled
      />
    </PendingDataProvider>
  );
}

export default Routes;
